// src/services/seqSync.js — write the server's order number onto the message.
//
// ══ WHY THIS EXISTS ════════════════════════════════════════════════════════
// Both clients order a chat by `seq`: a per-chat counter the signaling server
// assigns when it relays a message. It is the only key both sides agree on —
// two devices' clocks do not agree, and a timestamp written by one and read by
// the other reorders settled messages every time a snapshot lands.
//
// 🟥 BUT THE SERVER DOES NOT WRITE THE SEQ TO THE DOCUMENT. It reports it back
//    to the sender on a `message-seq` ack and moves on. Each sender is expected
//    to persist its own — which the phone does, in HomeScreen's `message-seq`
//    handler, with the comment "any device that cold-loads this chat gets the
//    correct order".
//
//    The web never listened. So every message ever sent from a browser had no
//    seq on its document, forever. The app then sorted those rows by TIMESTAMP
//    while sorting everything around them by SEQ (stores/messageOrder.ts:
//    seqAwareDesc falls through to time when one side has no seq) — two
//    different keys in one list, which is not an ordering at all. The rows land
//    wherever the comparisons happen to put them and shuffle again on the next
//    load. That is the "messages from the web jump around" report.
//
// ══ WHAT IT DOES ═══════════════════════════════════════════════════════════
// Listens for the ack and merges `{ seq }` onto chats/{chatId}/messages/{id}.
// One tiny write per sent message, on the sender's own document.
//
// 🟢 MERGE, AND ONLY THE SEQ. The send's own write already happened on this
//    same client, so FIFO ordering guarantees this lands after it and cannot
//    clobber anything. A merge of one field also means a doc already deleted by
//    store-and-forward is not resurrected with a half-message — the write
//    simply recreates nothing anyone reads, and Firestore's own rules refuse
//    what they refuse.

import { doc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { onMessageSeq } from './socket';

let stop = null;

export function startSeqSync() {
  if (stop) return stop;         // idempotent: one listener, however often this is called
  stop = onMessageSeq(async (ack) => {
    try {
      const chatId = ack && ack.chatId;
      const msgId = ack && (ack.serverId || ack.messageId);
      const seq = ack && ack.seq;
      if (!chatId || !msgId || seq == null) return;
      await setDoc(
        doc(db, 'chats', String(chatId), 'messages', String(msgId)),
        { seq: Number(seq) },
        { merge: true },
      );
    } catch (e) {
      // Never throw from a socket callback — a failed order write costs
      // ordering on a cold load, not the message.
      console.warn('[seq] could not persist seq:', e?.message || e);
    }
  });
  return stop;
}

export function stopSeqSync() {
  if (stop) { try { stop(); } catch {} stop = null; }
}
